import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import OnboardingForm from './components/OnboardingForm/OnboardingForm';
import SubmissionSuccess from './components/SubmissionSuccess/SubmissionSuccess';
import { FormProvider } from './context/FormContext';
import Auth0ProviderWithHistory, { useAuth } from './auth/Auth0ProviderWithHistory';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoginPage from './components/auth/LoginPage';
import { setAuth0TokenGetter } from './services/api';
import './App.css';

// Google Analytics helper function
const trackPageView = (path) => {
  if (window.gtag) {
    window.gtag('config', 'G-V8C9QRGV75', {
      page_path: path,
      linker: {
        domains: ['ngpes.com', 'start.ngpes.com']
      }
    });
  }
};

// Component to track route changes
const RouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
};

// Component that sets up API token getter after auth is ready
const AppWithAuth = () => {
  const { getAccessTokenSilently, isLoading, isAuthenticated } = useAuth();
  const [tokenSetup, setTokenSetup] = useState(false);

  // Debug auth state
  useEffect(() => {
    console.log('[App] Auth state changed:', {
      isLoading,
      isAuthenticated,
      hasTokenGetter: !!getAccessTokenSilently,
      tokenSetup
    });
  }, [isLoading, isAuthenticated, getAccessTokenSilently, tokenSetup]);

  // Set up the token getter as soon as auth is ready
  useEffect(() => {
    console.log('[App] Checking if ready to set up token getter...', {
      hasTokenGetter: !!getAccessTokenSilently,
      isLoading,
      isAuthenticated
    });

    if (getAccessTokenSilently && !isLoading && isAuthenticated) {
      console.log('[App] ✅ Setting up Auth0 token getter');
      setAuth0TokenGetter(getAccessTokenSilently);
      setTokenSetup(true);
    }
  }, [getAccessTokenSilently, isLoading, isAuthenticated]);

  // Don't render the FormProvider until auth is ready AND token getter is set
  if (isLoading) {
    console.log('[App] Still loading authentication...');
    return <div className="loading">Loading authentication...</div>;
  }

  if (!isAuthenticated) {
    console.log('[App] User not authenticated, rendering login route');
    // Reset token setup when user is not authenticated
    if (tokenSetup) {
      setTokenSetup(false);
    }
    // Render routes without FormProvider for unauthenticated users
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  if (!tokenSetup) {
    console.log('[App] Token getter not set up yet...');
    return <div className="loading">Setting up authentication...</div>;
  }

  console.log('[App] ✅ All ready, rendering FormProvider');

  return (
    <FormProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <OnboardingForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/success"
          element={
            <ProtectedRoute>
              <SubmissionSuccess />
            </ProtectedRoute>
          }
        />
      </Routes>
    </FormProvider>
  );
};

function App() {
  return (
    <div className="App">
      <Router>
        <RouteTracker />
        <Auth0ProviderWithHistory>
          <AppWithAuth />
        </Auth0ProviderWithHistory>
      </Router>
    </div>
  );
}

export default App;
